import { useState, useEffect } from 'react';
import { AiOutlineStop, AiOutlineMail } from 'react-icons/ai';
import './User.css';
import { useDispatch, useSelector } from 'react-redux';
import {
    inviteEmployee,
    suspendUser,
    revokeInvite,
    resendInvite,
    getInvitation,
    updateUserRole
} from '../../../../../modules/merchants/store/actions/action.creators';
import { authService } from '../../../../../modules/users/services/dependency.injection';
import { setSuccess, setError } from '../../../../../store/action';
import MessageModal from '../../../../../shared/components/MessageModal/MessageModal';
import { useAppSelector } from '../../../../../shared/infrastructure/store/hooks';

export default function UsersTab() {
    const dispatch = useDispatch();
    const invitations = useSelector(state => state.Merchants?.invitations || []);

    const { success, error } = useAppSelector(state => state.Util || {});

    const [errors, setErrors] = useState({});

    const [users, setUsers] = useState([]);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [actionUser, setActionUser] = useState(null);
    const [resendUser, setResendUser] = useState(null);
    const [roleChangeUser, setRoleChangeUser] = useState(null);
    const [selectedRole, setSelectedRole] = useState('');
    const [isFormValid, setIsFormValid] = useState(false);

    const [newUser, setNewUser] = useState({
        email: '',
        role: 'employee'
    });

    const merchantId = authService.getToken('merchant-id');

    useEffect(() => {
        if (success || error) {
            setShowErrorModal(true);
        }
    }, [success, error]);

    useEffect(() => {
        if (merchantId) {
            dispatch(getInvitation(merchantId));
        }
    }, [dispatch, merchantId]);

    useEffect(() => {
        if (invitations && invitations.length > 0) {
            const mappedUsers = invitations.map((inv, idx) => ({
                id: inv.userId || inv.id || idx,
                email: inv.email || inv.userEmail || '',
                role: inv.currentRole || inv.inviterole,
                expiresAt: inv.expiresAt,
                accepted: inv.accepted,
                status: inv.suspended === true ? 'suspended' : !inv.accepted ? 'pending' : 'accepted'
            }));
            setUsers(mappedUsers);
        }
    }, [invitations]);

    const mapInvitationStatus = apiStatus => {
        if (!apiStatus) return 'pending';
        const status = apiStatus.toLowerCase();
        if (status === 'accepted' || status === 'active') return 'accepted';
        if (status === 'suspended') return 'suspended';
        if (status === 'revoked') return 'revoked';
        return 'pending';
    };

    const confirmAction = () => {
        if (!actionUser) return;

        const merchantId = authService.getToken('merchant-id');
        if (!merchantId) {
            alert('Merchant ID not found');
            return;
        }

        let action;
        if (actionUser.status === 'pending') {
            action = revokeInvite({ merchantId, email: actionUser.email });
            dispatch(action);
            setActionUser(null);
            dispatch(getInvitation(merchantId));
            return;
        }

        if (actionUser.status === 'accepted' || actionUser.status === 'suspended') {
            const isCurrentlySuspended = actionUser.status === 'suspended';
            action = suspendUser({
                merchantId,
                userId: actionUser.id,
                isSuspended: !isCurrentlySuspended
            });
            dispatch(action);
            setActionUser(null);
            dispatch(getInvitation(merchantId));
            return;
        }
    };

    const confirmResend = () => {
        if (!resendUser) return;

        const merchantId = authService.getToken('merchant-id');
        if (!merchantId) {
            alert('Merchant ID not found');
            return;
        }

        dispatch(resendInvite({ email: resendUser.email }));

        dispatch(getInvitation(merchantId));

        setResendUser(null);
    };

    const isTokenExpired = user => {
        if (!user.expiresAt) return false;
        return new Date() > new Date(user.expiresAt) && !user.accepted;
    };

    // Handle invite
    const handleInvite = () => {
        if (!newUser.email) return;

        const merchantId = authService.getToken('merchant-id');

        if (!merchantId) {
            alert('Merchant ID not found');
            return;
        }

        dispatch(
            inviteEmployee({
                merchantId,
                email: newUser.email,
                role: newUser.role.toUpperCase()
            })
        );

        const newEntry = {
            id: Date.now(),
            email: newUser.email,
            role: newUser.role,
            status: 'pending'
        };

        setUsers(prev => [...prev, newEntry]);

        setShowInviteModal(false);
        setNewUser({ email: '', role: 'employee' });
    };

    // Handle role change click
    const handleRoleChangeClick = user => {
        setRoleChangeUser(user);
        setSelectedRole(user.role);
    };

    const confirmRoleChange = async () => {
        if (!roleChangeUser) return;

        const merchantId = authService.getToken('merchant-id');
        if (!merchantId) {
            alert('Merchant ID not found');
            return;
        }

        setUsers(prev => prev.map(u => (u.id === roleChangeUser.id ? { ...u, role: selectedRole } : u)));
        setRoleChangeUser(null);

        try {
            await dispatch(
                updateUserRole({
                    merchantId,
                    userId: roleChangeUser.id,
                    role: selectedRole.toUpperCase()
                })
            );

            dispatch(getInvitation(merchantId));
        } catch (err) {
            dispatch(getInvitation(merchantId));
        }
    };

    return (
        <div className="oi-team__background">
            <div className="oi-team__header">
                <div className="settings__header-column">
                    <h2 className="settings__header u-half-gradient-bg">{users.length > 1 ? 'Users' : 'User'}</h2>
                    <span className="oi-team__numbers">{users.length}</span>
                </div>

                <div className="oi-btn__containers oi-team__btn-wrapper" onClick={() => setShowInviteModal(true)}>
                    <button className="oi-btn__iconfy oi-btn--height3 oi-btn__style-add">Invite New User</button>
                </div>
            </div>

            <div className="oi-team__table-background">
                <table className="oi-table-pry">
                    <thead className="oi-table-pry__head">
                        <tr className="oi-table-pry__row">
                            <th className="oi-table-pry__header">
                                <span className="oi-table-pry__header-mod">S/N</span>
                            </th>
                            <th className="oi-table-pry__header">Email</th>
                            <th className="oi-table-pry__header">Role</th>
                            <th className="oi-table-pry__header">Status</th>
                            <th className="oi-table-pry__header">Actions</th>
                        </tr>
                    </thead>

                    <tbody className="oi-table-pry__body">
                        {users.map((user, idx) => (
                            <tr key={user.id} className="oi-table-pry__row">
                                <td className="oi-table-pry__data oi-table-pry__data-mod u-font-semibold">
                                    <span className="mobile-s_n">{idx + 1}</span>
                                </td>

                                <td className="oi-table-pry__data u-font-semibold">{user.email}</td>

                                <td
                                    className="oi-table-pry__data"
                                    onClick={() => handleRoleChangeClick(user)}
                                    style={{ cursor: 'pointer' }}>
                                    {user.role}
                                </td>

                                <td className="oi-table-pry__data">
                                    <span
                                        className={`status-badge ${
                                            user.status === 'accepted'
                                                ? 'accepted'
                                                : user.status === 'suspended'
                                                ? 'suspended'
                                                : user.status === 'revoked'
                                                ? 'revoked'
                                                : 'pending'
                                        }`}>
                                        {user.status === 'accepted'
                                            ? 'Accepted'
                                            : user.status === 'suspended'
                                            ? 'Suspended'
                                            : user.status === 'revoked'
                                            ? 'Revoked'
                                            : 'Pending'}
                                    </span>
                                </td>

                                {/* ACTION BUTTON */}
                                <td className="oi-table-pry__data">
                                    <div className="oi-button-flex">
                                        {(user.status === 'pending' ||
                                            user.status === 'accepted' ||
                                            user.status === 'suspended') && (
                                            <button
                                                className={`oi-btn icon-btn oi-btn--radius5 me-2 ${
                                                    user.status === 'pending' ? 'oi-btn--warning' : 'oi-btn--danger'
                                                }`}
                                                onClick={() => setActionUser(user)}
                                                title={
                                                    user.status === 'pending'
                                                        ? 'Revoke Invite'
                                                        : user.status === 'suspended'
                                                        ? 'Unsuspend User'
                                                        : 'Suspend User'
                                                }>
                                                <AiOutlineStop />
                                            </button>
                                        )}
                                        {user.status === 'pending' && isTokenExpired(user) && (
                                            <button
                                                className="oi-btn icon-btn oi-btn--success oi-btn--radius5"
                                                onClick={() => setResendUser(user)}
                                                title="Resend Invite">
                                                <AiOutlineMail />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* INVITE MODAL */}
            {showInviteModal && (
                <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h2 className="modal-title">Invite New User</h2>

                        <label>Email</label>
                        <input
                            type="email"
                            placeholder="Enter email"
                            value={newUser.email}
                            onChange={e => {
                                const email = e.target.value;
                                setNewUser({ ...newUser, email });
                                setIsFormValid(email.trim() && newUser.role);
                            }}
                        />

                        <label>Role</label>
                        <select
                            value={newUser.role}
                            onChange={e => {
                                const role = e.target.value;
                                setNewUser({ ...newUser, role });
                                setIsFormValid(newUser.email.trim() && role);
                            }}>
                            <option value="employee">EMPLOYEE</option>
                            <option value="merchant">MERCHANT</option>
                        </select>

                        <button
                            className={`submit-btn ${isFormValid ? 'submit-btn-active' : ''}`}
                            onClick={handleInvite}
                            disabled={!isFormValid}>
                            Invite
                        </button>
                    </div>
                </div>
            )}

            {actionUser && (
                <div className="modal-overlay" onClick={() => setActionUser(null)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h2 className="modal-title">
                            Confirm{' '}
                            {actionUser.status === 'pending'
                                ? 'Revoke'
                                : actionUser.status === 'suspended'
                                ? 'Unsuspend'
                                : 'Suspend'}
                        </h2>

                        <p className="modal-text">
                            Are you sure you want to{' '}
                            <strong>
                                {actionUser.status === 'pending'
                                    ? 'revoke'
                                    : actionUser.status === 'suspended'
                                    ? 'unsuspend'
                                    : 'suspend'}
                            </strong>{' '}
                            this user?
                        </p>

                        <div className="modal-actions">
                            <button className="oi-btn oi-btn--secondary" onClick={() => setActionUser(null)}>
                                Cancel
                            </button>

                            <button className="oi-btn oi-btn--danger" onClick={confirmAction}>
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {resendUser && (
                <div className="modal-overlay" onClick={() => setResendUser(null)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h2 className="modal-title">Resend Invite</h2>
                        <p className="modal-text">
                            Resend invitation to <strong>{resendUser.email}</strong>?
                        </p>
                        <div className="modal-actions">
                            <button className="oi-btn oi-btn--secondary" onClick={() => setResendUser(null)}>
                                Cancel
                            </button>
                            <button className="oi-btn oi-btn--success" onClick={confirmResend}>
                                Resend
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {roleChangeUser && (
                <div className="modal-overlay" onClick={() => setRoleChangeUser(null)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h2 className="modal-title">Change User Role</h2>
                        <p className="modal-text">
                            Select new role for <strong>{roleChangeUser.email}</strong>:
                        </p>

                        <label>Role</label>
                        <select value={selectedRole} onChange={e => setSelectedRole(e.target.value)}>
                            <option value="employee">EMPLOYEE</option>
                            <option value="Merchant">MERCHANT</option>
                        </select>

                        <div className="modal-actions">
                            <button className="oi-btn oi-btn--secondary" onClick={() => setRoleChangeUser(null)}>
                                Cancel
                            </button>
                            <button className="oi-btn oi-btn--success" onClick={confirmRoleChange}>
                                Update Role
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showErrorModal && (
                <MessageModal
                    type={success ? 'success' : 'error'}
                    message={success || error}
                    close={() => {
                        setShowErrorModal(false);
                        dispatch(setSuccess(''));
                        dispatch(setError(''));
                    }}
                />
            )}
        </div>
    );
}
